import React, { useEffect, useMemo, useState } from "react";
import GModal from "../design/components/GModal";
import Button from "../design/Button";
import {
  CustomSelect,
  CustomTooltip,
} from "../design/components/customComponents";
import { useSelector } from "react-redux";
import classes from "./AddVirtualNumberModal.module.css";
import {
  chevronDown,
  chevronLeft,
  fileIcon,
  minusIcon,
  plusIcon,
} from "../../utility-functions/assetLoader";
import { JService } from "../api-service/ApiService";
import { enqueueSnackbar } from "notistack";
import { getOrgURLParam, sentenceCase } from "../../utility-functions";
import { generateProformaInvoicePdf } from "../billing/GenerateProformaInvoicePDF";

const isValidNumber = (val) =>
  !isNaN(Number(val)) && val !== null && val !== "";

const billingCycleMap = {
  WEEKLY: 0.25,
  MONTHLY: 1,
  QUARTERLY: 3,
  BIANNUAL: 6,
  ANNUAL: 12,
};

const AddVirtualNumberModal = ({
  visible,
  onClose,
  handleOpenChatWidget,
  purchaseHandler,
}) => {
  const reduxSubscription = useSelector(
    (state) => state?.account?.value?.subscription
  );
  const locations = useSelector((state) => state?.locations?.value);
  const currency = reduxSubscription?.plan?.currency === "USD" ? "$" : "₹";
  const billingCycle = reduxSubscription?.plan?.billing_cycle;

  //States
  const [selectedCountry, setSelectedCountry] = useState(currency === '$' ? '' : "India");
  const [selectedNumberType, setSelectedNumberType] = useState("");
  const [selectedNumberTypeInfo, setSelectedNumberTypeInfo] = useState({});
  const [showSelectedNumberDetails, setShowSelectedNumberDetails] =
    useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedCountryData, setSelectedCountryData] = useState({});

  const [vnData, setVnData] = useState({});
  const [showProformaSection, setShowProformaSection] = useState(false);
  const [proformaLoading, setProformaLoading] = useState(false);

  //Handlers
  const selectCountryHandler = (e) => {
    setSelectedCountry(e.target.value);
  };

  const handleDownloadProformaInvoice = () => {
    setProformaLoading(true);
    const url = `/api/v1/subscriptions/proforma-invoice/${getOrgURLParam()}`;
    JService.post(url, JSON.stringify({
      items: [{
        type: "virtual_number",
        quantity: Number(quantity),
        vn_country: selectedCountry,
        vn_type: selectedNumberType,
      }]
    }))
      .then((res) => {
        if (!res?.success || !res?.proforma_invoice) {
          throw new Error(res?.message || "Failed to generate proforma invoice");
        }
        generateProformaInvoicePdf(res.proforma_invoice);
      })
      .catch((err) => {
        console.error("Proforma invoice error:", err);
        enqueueSnackbar(err?.message || "Could not generate proforma invoice", {
          variant: "error",
        });
      })
      .finally(() => {
        setProformaLoading(false);
      });
  };

  //Effects

  useEffect(() => {
    setSelectedNumberType("");
    setSelectedNumberTypeInfo({});
    setSelectedCountryData(
      vnData?.pricing?.find((v) => v.country === selectedCountry)
    );
  }, [selectedCountry, vnData]);

  useEffect(() => {
    if (!selectedNumberType) return;
    setSelectedNumberTypeInfo(
      selectedCountryData?.number_types?.find(
        (nt) => nt.type === selectedNumberType
      )
    );
  }, [selectedNumberType, selectedCountryData]);

  useEffect(() => {
    const url = `/api/v1/subscriptions/retrieve-vn-pricing/${getOrgURLParam()}?country=${selectedCountry}`;
    JService.get(url)
      .then((response) => {
        if (!response?.success) {
          throw new Error(response?.message);
        }
        setVnData(response?.data || {});
      })
      .catch((error) => {
        console.error("Retrieve vn pricing error = ", error);
        enqueueSnackbar(
          error?.message || "Failed to retrieve virtual number pricing",
          {
            variant: "error",
          }
        );
      });
  }, [selectedCountry]);

  const freeLicensesLeft = Math.max(
    0,
    (vnData?.plan_licenses ?? 0) - (vnData?.free_vns_claimed ?? 0)
  );

  let payableLicenses = quantity;
  if (selectedNumberType?.toLowerCase() === 'standard') {
    if (currency === '₹' || (currency === '$' && ["united states", "united kingdom", "canada"].includes(
      selectedCountry?.toLowerCase()
    )))
      payableLicenses = Math.max(0, quantity - freeLicensesLeft);
  }

  const perNumberCost =
    (isValidNumber(selectedNumberTypeInfo?.discounted_number_rental)
      ? Number(selectedNumberTypeInfo?.discounted_number_rental)
      : 0);

  const totalCost =
    Math.round(
      ((payableLicenses * Number(selectedNumberTypeInfo?.one_time_fee)) +
        (payableLicenses * Number(perNumberCost).toFixed(2) * billingCycleMap[billingCycle])) * 100
    ) / 100;

  const countryOptions = useMemo(() => {
    return locations
      ?.filter(loc => !(currency === '$' && loc.country === "India"))
      .map(loc => ({ value: loc.country, displayValue: loc.country }));
  }, [locations, currency]);
  return (
    <GModal
      visible={visible}
      closeModal={onClose}
      heading="Add number"
      modalStyle={{ width: "440px" }}
      bodyWrapperStyle={{ paddingRight: "0", maxHeight: "500px" }}
      bottomButtons={[
        <Button
          variant="outlined"
          buttonText="Cancel"
          disabled={false}
          isLoading={false}
          hierarchy="white"
          onClick={onClose}
        />,
        showSelectedNumberDetails ? (
          <Button
            variant="filled"
            buttonText="Purchase"
            isLoading={false}
            hierarchy="green"
            onClick={() => {
              onClose();
              purchaseHandler(
                {
                  vn_addon_type: selectedNumberType,
                  vn_addon_country: selectedCountry,
                  quantity: quantity,
                },
                payableLicenses,
              );
            }}
          />
        ) : (
          <Button
            variant="filled"
            buttonText="Next"
            isLoading={false}
            hierarchy="green"
            disabled={
              !selectedCountry ||
              !selectedNumberType ||
              ["contact sales team", "not available"].includes(
                selectedNumberTypeInfo?.number_rental?.toLowerCase(),
              ) ||
              Number(selectedNumberTypeInfo?.quantity) === 0 ||
              (selectedCountry?.toLowerCase() === "india" &&
                selectedNumberType?.toLowerCase() === "mobile")
            }
            onClick={() => setShowSelectedNumberDetails(true)}
          />
        ),
      ]}
      body={
        showSelectedNumberDetails ? (
          <div>
            <button
              className={classes.backBtn}
              onClick={() => setShowSelectedNumberDetails(false)}
            >
              {chevronLeft("20", "20", "#3F3F46")}
              <span>Back</span>
            </button>
            <div className={classes.vnInfo}>
              <h6>
                {selectedCountry} - {sentenceCase(selectedNumberType)}
              </h6>
            </div>
            {selectedCountryData?.document_required ? (
              <div className={classes.docVerification}>
                <span>Document verification required after purchase.</span>
                <br />
                <a href="https://knowledge.frejun.com/what-documentation-is-required-to-purchase-an-indian-virtual-number">
                  Check required docs.
                </a>
                {/* There is no article for other country virtual numbers, neither a single article for all types of numbers*/}
              </div>
            ) : null}
            <div className={classes.quantity}>
              <span>Quantity</span>
              <div>
                <button
                  disabled={quantity === 1}
                  onClick={() => setQuantity((prev) => prev - 1)}
                  style={{ background: quantity === 1 ? "#FAFAFA" : "#ffffff" }}
                >
                  {minusIcon("20", "20")}
                </button>
                <span>{quantity}</span>
                <CustomTooltip
                  placement="top"
                  tooltipTitle={
                    quantity === +selectedNumberTypeInfo?.quantity ? (
                      <p>
                        Need more than {quantity} numbers?{" "}
                        <button
                          className={classes.contactSupport}
                          onClick={() => {
                            onClose();
                            handleOpenChatWidget();
                          }}
                        >
                          Contact support
                        </button>{" "}
                      </p>
                    ) : (
                      ""
                    )
                  }
                >
                  <button
                    disabled={quantity === +selectedNumberTypeInfo?.quantity}
                    onClick={() => setQuantity((prev) => prev + 1)}
                    style={{
                      background:
                        quantity === +selectedNumberTypeInfo?.quantity
                          ? "#FAFAFA"
                          : "#ffffff",
                    }}
                  >
                    {plusIcon("#3F3F46")}
                  </button>
                </CustomTooltip>
              </div>
            </div>
            <div className={classes.separator} />
            {isValidNumber(
              selectedNumberTypeInfo?.discounted_number_rental,
            ) && (
              <div className={classes.priceContainer}>
                <h6>Price per number</h6>
                <span>
                  {currency}
                  {selectedNumberTypeInfo?.discounted_number_rental}/month
                </span>
              </div>
            )}
            {isValidNumber(selectedNumberTypeInfo?.one_time_fee) && (
              <div className={classes.priceContainer}>
                <h6>One-time setup fee</h6>
                <span>
                  {currency}
                  {selectedNumberTypeInfo?.one_time_fee}/number
                </span>
              </div>
            )}
            <div className={classes.separator} />
            {(isValidNumber(selectedNumberTypeInfo?.one_time_fee) ||
              isValidNumber(
                selectedNumberTypeInfo?.discounted_number_rental,
              )) && (
              <>
                <div className={classes.total}>
                  <h6>Total</h6>
                  <span>
                    {currency}
                    {totalCost}
                  </span>
                </div>
                <div className={classes.totalMessage}>
                  {!payableLicenses ? (
                    <p>
                      Your plan includes 1 free virtual number per plan license.
                      All numbers in this purchase are free.
                    </p>
                  ) : payableLicenses < quantity ? (
                    <p>
                      Your plan includes 1 free virtual number per plan license.
                      You are paying for <strong>{payableLicenses}</strong>{" "}
                      numbers.
                    </p>
                  ) : null}
                </div>
              </>
            )}
            {totalCost > 0 && (
              <div className={classes.proformaSection}>
                <button
                  className={classes.proformaToggle}
                  onClick={() => setShowProformaSection((prev) => !prev)}
                >
                  Need proforma invoice?
                  <span
                    style={{
                      transform: showProformaSection
                        ? "rotate(180deg)"
                        : "none",
                      display: "inline-flex",
                    }}
                  >
                    {chevronDown("#2AA251", 20, 20)}
                  </span>
                </button>
                {showProformaSection && (
                  <div className={classes.proformaCard}>
                    <p className={classes.proformaDesc}>
                      Download a quote PDF to share with your finance team.
                    </p>
                    <button
                      className={classes.proformaDownloadBtn}
                      onClick={handleDownloadProformaInvoice}
                      disabled={proformaLoading}
                    >
                      {proformaLoading ? (
                        <div className={classes.loader}>
                          <div className={classes.spinner}></div>
                        </div>
                      ) : (
                        fileIcon("#3F3F46", 20, 20)
                      )}
                      Download proforma invoice (PDF)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div>
            <CustomSelect
              defaultValue={"Select country"}
              value={selectedCountry}
              options={countryOptions}
              selectStyles={{
                borderRadius: "6px",
                marginBottom: "20px",
              }}
              handleChange={selectCountryHandler}
              label="Country"
              disabled={currency === "₹"}
            />

            <CustomSelect
              defaultValue="Select type"
              value={selectedNumberType}
              options={selectedCountryData?.number_types?.map((nt) => {
                return { value: nt.type, displayValue: sentenceCase(nt.type) };
              })}
              selectStyles={{
                borderRadius: "6px",
              }}
              disabled={!selectedCountryData?.number_types?.length}
              handleChange={(e) => setSelectedNumberType(e.target.value)}
              label="Number type"
            />
            {selectedNumberType?.toLowerCase() === "mobile" &&
            selectedCountry?.toLowerCase() === "india" ? (
              <div className={classes.docVerification}>
                <span>
                  {" "}
                  Indian mobile numbers (DIDs) are provided as part of
                  customized enterprise solutions:{" "}
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://knowledge.frejun.com/does-frejun-offer-mobile-numbers-what-are-the-conditions"
                  >
                    Learn more
                  </a>
                </span>{" "}
                <br />
                To purchase, please{" "}
                <button
                  className={classes.contactSupport}
                  onClick={() => {
                    onClose();
                    handleOpenChatWidget();
                  }}
                >
                  Contact support.
                </button>
              </div>
            ) : ["contact sales team", "not available"].includes(
                selectedNumberTypeInfo?.number_rental?.toLowerCase(),
              ) || Number(selectedNumberTypeInfo?.quantity) === 0 ? (
              <div className={classes.notAvailableInfo}>
                <span>Number unavailable - our team can order it for you.</span>{" "}
                <button
                  className={classes.contactSupport}
                  onClick={() => {
                    onClose();
                    handleOpenChatWidget();
                  }}
                >
                  Contact support.
                </button>
              </div>
            ) : null}
          </div>
        )
      }
    />
  );
};

export default AddVirtualNumberModal;
